import Phaser from 'phaser';
import type { ShimmerMode } from './ShimmerClock.js';

export interface ShimmerRendererBounds {
  /** Width of the pill's rounded-rect interior, in container-local px. */
  width: number;
  /** Height of the pill's rounded-rect interior, in container-local px. */
  height: number;
  /** Corner radius — pass `height / 2` for a true pill, 0 for a sharp rect. */
  radius: number;
}

export interface ShimmerRendererOpts {
  scene: Phaser.Scene;
  /** Container the pill's bg/text live in. Renderer adds its Graphics here so
   *  the shimmer follows any parent transforms automatically. */
  parent: Phaser.GameObjects.Container;
  bounds: ShimmerRendererBounds;
  /** Renderer Graphics depth — set above the pill bg/text so the shimmer
   *  reads on top. */
  depth: number;
}

export interface ShimmerRenderer {
  render(localPhase: number, peakAlpha: number): void;
  clear(): void;
  destroy(): void;
}

const SHIMMER_GOLD = 0xd4af37;

export class BandShimmerRenderer implements ShimmerRenderer {
  private gfx: Phaser.GameObjects.Graphics;
  private maskGfx: Phaser.GameObjects.Graphics;
  private parent: Phaser.GameObjects.Container;
  private bounds: ShimmerRendererBounds;
  private depth: number;

  constructor(opts: ShimmerRendererOpts) {
    this.bounds = opts.bounds;
    this.parent = opts.parent;
    this.depth = opts.depth;

    // Mask + band live on the SCENE root, not inside the parent container.
    // Phaser's GeometryMask doesn't reliably inherit container transforms in
    // the WebGL stencil path, so we drive their positions ourselves each
    // render() call from the parent container's world coords. This keeps
    // the masked-band region correctly aligned with the pill's actual
    // on-screen position.
    this.maskGfx = opts.scene.add.graphics();
    this.maskGfx.fillStyle(0xffffff, 1);
    this.maskGfx.fillRoundedRect(
      -this.bounds.width / 2,
      -this.bounds.height / 2,
      this.bounds.width,
      this.bounds.height,
      this.bounds.radius,
    );
    this.maskGfx.setVisible(false);

    this.gfx = opts.scene.add.graphics();
    // Sit just under the badge text — the pill's text container is rendered
    // at the parent container's depth + 0 child order; depth here is the
    // host's preferred z-relative-to-pill (host passes depth=1 currently,
    // but since this graphics is on the scene root we add the parent's
    // depth so the shimmer renders above the badge bg but below the
    // following scene chrome). Practical effect: keep band visible.
    this.gfx.setDepth((this.parent.depth ?? 0) + this.depth);
    this.gfx.setMask(this.maskGfx.createGeometryMask());
  }

  render(localPhase: number, peakAlpha: number): void {
    // Sync to parent's world coords each frame so the mask + band track the
    // badge as the chip stack moves (e.g. ChipStackVisual.tweenTo).
    const wx = this.parent.x;
    const wy = this.parent.y;
    this.maskGfx.setPosition(wx, wy);
    this.gfx.setPosition(wx, wy);

    const { width: w, height: h } = this.bounds;
    // Wider band so the sweep reads as a glow, not a stripe. ~55% of pill
    // width gives room for a soft alpha falloff on each side of the peak.
    const bandW = Math.max(6, w * 0.55);
    const travel = w + bandW * 2;
    const cx = -bandW + travel * localPhase - w / 2;

    this.gfx.clear();
    // Approximate a 0 → peak → 0 gradient across the band's width with N
    // sine-weighted strips. With N=14 each strip is ~bandW/14 wide and the
    // hard alpha steps anti-alias into a smooth gradient at modern zoom
    // levels. Vertical extent overshoots the pill height so the −45° lean
    // covers the rounded-rect mask interior at every localPhase.
    const N = 14;
    const stripW = bandW / N;
    const half = h;
    for (let i = 0; i < N; i++) {
      const t = (i + 0.5) / N; // strip-center position, in (0, 1)
      const a = peakAlpha * Math.sin(t * Math.PI);
      const x = cx - bandW / 2 + i * stripW;
      drawTiltedRect(this.gfx, x, -half, stripW, half * 2, a);
    }
  }

  clear(): void {
    this.gfx.clear();
  }

  destroy(): void {
    this.gfx.clearMask(true);
    this.gfx.destroy();
    this.maskGfx.destroy();
  }
}

/**
 * Draws a vertical strip and applies a −45° shear by offsetting the bottom
 * vertices. Called as a helper rather than using rotate-around-pivot so the
 * three strips share an axis without per-strip transform setup.
 */
function drawTiltedRect(
  gfx: Phaser.GameObjects.Graphics,
  x: number,
  y: number,
  w: number,
  h: number,
  alpha: number,
): void {
  if (alpha <= 0) return;
  gfx.fillStyle(SHIMMER_GOLD, alpha);
  // Diagonal: each unit of y shifts x by +1 (45° lean).
  gfx.beginPath();
  gfx.moveTo(x, y);
  gfx.lineTo(x + w, y);
  gfx.lineTo(x + w + h, y + h);
  gfx.lineTo(x + h, y + h);
  gfx.closePath();
  gfx.fillPath();
}

export function createShimmerRenderer(
  mode: Exclude<ShimmerMode, 'off'>,
  opts: ShimmerRendererOpts,
): ShimmerRenderer {
  switch (mode) {
    case 'band': return new BandShimmerRenderer(opts);
    case 'rim':  return new RimShimmerRenderer(opts);
  }
}

const SHIMMER_RIM_HIGHLIGHT = 0xfff7c2;

export class RimShimmerRenderer implements ShimmerRenderer {
  private gfx: Phaser.GameObjects.Graphics;
  private bounds: ShimmerRendererBounds;

  constructor(opts: ShimmerRendererOpts) {
    this.bounds = opts.bounds;
    this.gfx = opts.scene.add.graphics();
    this.gfx.setDepth(opts.depth);
    opts.parent.add(this.gfx);
  }

  render(localPhase: number, peakAlpha: number): void {
    const { width: w, height: h } = this.bounds;
    // Diagonal sweep position: localPhase=0 → top-left corner, localPhase=1 →
    // bottom-right corner. Use −45° (slope = -1 from top-left to bottom-right
    // in screen coords, i.e. +1 in local y-down coords).
    const startX = -w / 2;
    const startY = -h / 2;
    const endX = w / 2;
    const endY = h / 2;
    const cx = startX + (endX - startX) * localPhase;
    const cy = startY + (endY - startY) * localPhase;

    // Stroke a short chord through (cx, cy) along the perpendicular to the
    // sweep direction, clipped to the pill's bounding rect. With a −45° sweep
    // the perpendicular is a +45° line, so chord points are (cx-d, cy-d) and
    // (cx+d, cy+d) clamped to the rect interior.
    const halfChord = Math.min(w, h) * 0.5;
    const ax = Math.max(-w / 2, Math.min(w / 2, cx - halfChord));
    const ay = Math.max(-h / 2, Math.min(h / 2, cy - halfChord));
    const bx = Math.max(-w / 2, Math.min(w / 2, cx + halfChord));
    const by = Math.max(-h / 2, Math.min(h / 2, cy + halfChord));

    this.gfx.clear();
    const lineWidth = Math.max(1.5, h * 0.12);
    this.gfx.lineStyle(lineWidth, SHIMMER_RIM_HIGHLIGHT, peakAlpha);
    this.gfx.beginPath();
    this.gfx.moveTo(ax, ay);
    this.gfx.lineTo(bx, by);
    this.gfx.strokePath();
  }

  clear(): void {
    this.gfx.clear();
  }

  destroy(): void {
    this.gfx.destroy();
  }
}
